import React from 'react';
import { View } from 'react-native-web';
import TemplatePage from './TemplatePage';
import { colors, typography } from '../../utils/styling';

// F2 (McKayla Loom "Current Site Feedback Part 1" t=0:27 and Part 2 t=4:01,
// ClickUp 86bah7dqn). Verbatim on the fields: "They say their name, their
// company name, email, phone number, how many advisors, and what state they're
// in. I think we need like exactly this."
//
// FIELDS is those seven, in her order, matching Nitrogen's form
// (frames/CSF2-t0410-nitrogen-book-demo-form.jpg). "exactly this" is the
// request — do not add, drop, or reorder a field without her.
//
// ⛔ THE FORM IS DELIBERATELY NOT SUBMITTABLE, AND THAT IS THE SAFE STATE.
// Plan question Q1 is open and is hers to answer: "Where does a Book a Demo
// submission go, and who runs the demo?" Her own words were "I guess me right
// now. That's something we're going to have to find out who's going to be
// doing the demos." Until there is a named destination and a named owner,
// there is nowhere to send a submission.
//
// This page is live on production, where a real advisory firm can reach it. A
// form that accepts a lead and drops it on the floor is strictly worse than a
// form that says it is not open yet: the visitor believes they have asked for
// a demo, waits, and hears nothing. Wire the submit handler only once Q1 has
// an answer — and when you do, delete this comment block and the disabled
// state together, not just the disabled state.
export const FIELDS: Array<{ label: string; type: string; autoComplete?: string }> = [
  { label: 'First name', type: 'text', autoComplete: 'given-name' },
  { label: 'Last name', type: 'text', autoComplete: 'family-name' },
  { label: 'Company name', type: 'text', autoComplete: 'organization' },
  { label: 'Email address', type: 'email', autoComplete: 'email' },
  { label: 'Phone number', type: 'tel', autoComplete: 'tel' },
  { label: 'Number of advisors at your firm', type: 'text' },
  { label: 'State', type: 'text', autoComplete: 'address-level1' },
];

export default function BookDemo() {
  return (
    <TemplatePage
      title="Book a demo"
      intro="See how Money Personality works for an advisory team. These are the details we would ask for."
    >
      <View style={styles.notice}>
        <span style={styles.noticeText}>
          Demo requests are not open yet — this form does not send. It is here so the fields can
          be reviewed before it goes live.
        </span>
      </View>

      <form
        style={styles.form}
        onSubmit={(e) => {
          // No destination exists yet (Q1). Refusing the submit is the point:
          // never accept a lead we cannot deliver to a person.
          e.preventDefault();
        }}
      >
        {FIELDS.map((field) => (
          <label key={field.label} style={styles.field}>
            <span style={styles.label}>{field.label}</span>
            <input
              type={field.type}
              name={field.label}
              autoComplete={field.autoComplete}
              disabled
              style={styles.input}
            />
          </label>
        ))}

        <button type="submit" disabled style={styles.button}>
          Request a demo
        </button>
      </form>
    </TemplatePage>
  );
}

const styles = {
  notice: {
    marginTop: 32,
    backgroundColor: colors.gray[100],
    border: `1px solid ${colors.gray[200]}`,
    borderRadius: 8,
    padding: 16,
  } as React.CSSProperties,
  noticeText: {
    fontFamily: typography.fontFamily.body,
    fontSize: 15,
    color: colors.gray[700],
  } as React.CSSProperties,
  form: {
    marginTop: 32,
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    maxWidth: 520,
  } as React.CSSProperties,
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  } as React.CSSProperties,
  label: {
    fontFamily: typography.fontFamily.body,
    fontSize: 14,
    fontWeight: 600,
    color: colors.dark,
  } as React.CSSProperties,
  input: {
    fontFamily: typography.fontFamily.body,
    fontSize: 16,
    padding: '10px 12px',
    borderRadius: 6,
    border: `1px solid ${colors.gray[300]}`,
    backgroundColor: colors.gray[100],
    color: colors.gray[600],
  } as React.CSSProperties,
  button: {
    marginTop: 8,
    alignSelf: 'flex-start',
    fontFamily: typography.fontFamily.body,
    fontSize: 16,
    fontWeight: 700,
    color: colors.white,
    backgroundColor: colors.gray[400],
    border: 'none',
    borderRadius: 6,
    padding: '12px 24px',
    cursor: 'not-allowed',
  } as React.CSSProperties,
} as const;
